import { Icon } from '@ps/isp-web-component-library';
import { BetBuilderBadge } from '@ps/isp-web-component-library/icons/BetBuilderBadge';
import { ChevronMediumRight } from '@ps/isp-web-component-library/icons/ChevronMediumRight';
import { LiveStreamingBadge } from '@ps/isp-web-component-library/icons/LiveStreamingBadge';
import { PriceBoost } from '@ps/isp-web-component-library/icons/PriceBoost';
import { StarSubIcon } from '@ps/isp-web-component-library/icons/StarSubIcon';
import type { EventBasedMarketView, EventView } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import {
  TrackingContext,
  type TrackingContextValue,
  sportsCompetition,
  sportsEvent,
  sportsMarket,
  sportsSport
} from '@ps/web-shared-analytics';
import { Link, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { RootState } from '@swf/core/store';
import { Selection } from '@swf/sports/components';
import EventStatus from '@swf/sports/components/EventStatus/EventStatus';
import useEventStatus from '@swf/sports/components/EventStatus/hooks/useEventStatus';
import EventStructuredData from '@swf/sports/components/EventStructuredData/EventStructuredData';
import Scoreboard from '@swf/sports/components/Scoreboard/Scoreboard';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isOddsBoostMarket } from '@swf/sports/utils/isOddsBoostMarket';
import { isValidInPlayStatus } from '@swf/sports/utils/isValidInPlayStatus';
import mergeWithTrackingContextValue from '@swf/sports/utils/mergeWithTrackingContextValue';
import useIsEventTypeLiveDataEnabled from '@swf/sports/utils/useIsEventTypeLiveDataEnabled';

import {
  TXTWCL_Bet_Builder,
  TXTWCL_Live_Stream,
  TXTWCL_More_markets,
  TXTWCL_Star_Sub_selections,
  TXTWCL_odds_boost_selections
} from './Event.messages.json';
import styles from './Event.scss';
import Participants from './Participants/Participants';

const appendTrackingEntities = (
  trackingContext: TrackingContextValue,
  event: EventView,
  market?: EventBasedMarketView
): TrackingContextValue =>
  mergeWithTrackingContextValue(trackingContext, [
    sportsEvent({
      eventId: event.eventId,
      eventStartTime: new Date(event.eventStartTime).getTime()
    }),
    sportsMarket({
      marketId: market?.marketId ?? '',
      marketInPlay: Boolean(market?.inPlay),
      marketIsOddsBoost: isOddsBoostMarket(market?.marketType)
    }),
    sportsCompetition({ competitionId: event.competitionId }),
    sportsSport({ sportId: event.eventTypeId })
  ]);

export interface Props {
  event: EventView;
  market?: EventBasedMarketView;
}

const Event: React.FC<Props> = ({ event, market }) => {
  const isBetBuilderEnabled = useFeature(FeatureFlag.ISPBetBuilder);
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const {
    competitionId,
    enrichedParticipants,
    eventId,
    eventName,
    eventSlug,
    eventStartTime,
    eventTypeId,
    hasLiveStreamAvailable,
    hasStarSubEnabledMarkets,
    oddsBoostCount
  } = event;

  const numberOfRunners = market?.runners.length ?? 0;

  const eventType = useSelector((state: RootState) => widgetSelect.eventType(state, eventTypeId));
  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, competitionId)
  );

  const { competitionSlug } = competition;
  const { eventTypeSlug } = eventType;

  const { formatMessage } = useRegulatoryIntl();
  const trackingContextValue = useContext(TrackingContext);
  const eventStatus = useEventStatus({
    inPlay: market?.inPlay,
    startTime: eventStartTime,
    canTurnInPlay: market?.offeredInPlay
  });

  const isScoreboardAvailable =
    useIsEventTypeLiveDataEnabled(eventTypeId) && isValidInPlayStatus(eventStatus);

  return (
    <>
      <TrackingContext.Provider value={appendTrackingEntities(trackingContextValue, event)}>
        <div
          className={classNames(styles.container, {
            [styles.inPlay]: isValidInPlayStatus(eventStatus)
          })}
        >
          <div
            className={classNames(styles.event, {
              [styles.twoRunnerMarket]: numberOfRunners === 2,
              [styles.threeRunnerMarket]: numberOfRunners === 3
            })}
          >
            <div className={styles.game}>
              <Link
                className={styles.teams}
                alias="sportsEvent"
                values={{
                  eventTypeSlug,
                  eventTypeId,
                  competitionSlug,
                  competitionId,
                  eventSlug,
                  eventId
                }}
              >
                {isScoreboardAvailable ? (
                  <Scoreboard
                    enrichedParticipants={enrichedParticipants}
                    eventId={eventId}
                    eventTypeId={eventTypeId}
                    eventName={eventName}
                  />
                ) : (
                  <Participants
                    eventTypeId={eventTypeId}
                    eventName={eventName}
                    enrichedParticipants={enrichedParticipants}
                  />
                )}
              </Link>
            </div>

            {market && numberOfRunners > 0 ? (
              <div className={styles.selections}>
                {market.runners.map((runner) => (
                  <Selection
                    competitionId={competitionId}
                    eventId={eventId}
                    eventTypeId={eventTypeId}
                    handicap={runner.handicap}
                    key={runner.selectionId}
                    legTypes={market.legTypes}
                    marketId={market.marketId}
                    marketStatus={market.marketStatus}
                    marketType={market.marketType}
                    odds={runner.winRunnerOdds}
                    runnerResultType={runner.resultType}
                    runnerStatus={runner.runnerStatus}
                    selectionId={runner.selectionId}
                    startTime={eventStartTime}
                  />
                ))}
              </div>
            ) : null}
          </div>

          <div className={styles.bottomRow}>
            <div className={styles.status}>
              <EventStatus
                isInPlay={market?.inPlay}
                startTime={eventStartTime}
                isTurnInPlay={market?.offeredInPlay}
                eventId={eventId}
                eventTypeId={eventTypeId}
              />
            </div>

            <div className={styles.icons}>
              {hasStarSubEnabledMarkets ? (
                <Icon
                  icon={StarSubIcon}
                  label={formatMessage(TXTWCL_Star_Sub_selections)}
                  size="sm"
                />
              ) : null}

              {oddsBoostCount ? (
                <Icon
                  icon={PriceBoost}
                  label={formatMessage(TXTWCL_odds_boost_selections)}
                  size="sm"
                />
              ) : null}

              {isBetBuilderEnabled && market?.isSgmEligible ? (
                <Icon icon={BetBuilderBadge} label={formatMessage(TXTWCL_Bet_Builder)} size="sm" />
              ) : null}

              {hasLiveStreamAvailable ? (
                <Icon
                  icon={LiveStreamingBadge}
                  label={formatMessage(TXTWCL_Live_Stream)}
                  size="sm"
                />
              ) : null}
            </div>

            {!market ? (
              <Link
                className={styles.moreLink}
                alias="sportsEvent"
                values={{
                  eventTypeSlug,
                  eventTypeId,
                  competitionSlug,
                  competitionId,
                  eventSlug,
                  eventId
                }}
              >
                <Translation {...TXTWCL_More_markets} />
                <Icon icon={ChevronMediumRight} size="sm" />
              </Link>
            ) : null}
          </div>
        </div>
      </TrackingContext.Provider>

      {market && (
        <EventStructuredData
          event={{
            ...event,
            eventStartTime,
            isInPlay: market.inPlay
          }}
          market={{
            ...market,
            eventId,
            eventTypeId,
            marketType: market.marketType || '',
            marketTime: eventStartTime,
            isStartingPriceMarket: false
          }}
          eventType={eventType}
          competition={competition}
        />
      )}
    </>
  );
};

export { Event };
